import React, { useEffect, useRef, useState } from 'react';
import { Link } from "react-router-dom";
import { useLanguage } from '../../i18n';
import { useTheme } from '../../theme';

const Navbar = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const languageMenuRef = useRef(null);
  const { currentLanguage, languages, setLanguage, t, withLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const closeMenu = (event) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setIsLanguageOpen(false);
      }
    };

    document.addEventListener('mousedown', closeMenu);
    return () => document.removeEventListener('mousedown', closeMenu);
  }, []);

  const handleLanguageSelect = (languageCode) => {
    setLanguage(languageCode);
    setIsLanguageOpen(false);
  };

  return (
    <nav style={styles.navbar}>
      <Link to={withLanguage("/")} style={styles.logoLink}>
        <img src="/images/fixanor logo.png" style={styles.navbarHomeImage} alt="Fixanor logo" />
      </Link>

      <div style={{ ...styles.contactContainer, gap: isMobile ? '0.75rem' : '2rem' }}>
        <a href="tel:+40774924548" style={styles.contactLink}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="24"
            viewBox="0 0 24 24"
            width="24"
            fill="currentColor"
            style={styles.icon}
          >
            <path d="M0 0h24v24H0V0z" fill="none" />
            <path d="M17 1H7c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2zm0 18H7V5h10v14z" />
          </svg>
          {!isMobile && '+40 774 924 548'}
        </a>

        <div style={styles.languageWrapper} ref={languageMenuRef}>
          <button
            type="button"
            style={styles.languageButton}
            onClick={() => setIsLanguageOpen((isOpen) => !isOpen)}
            aria-haspopup="menu"
            aria-expanded={isLanguageOpen}
            aria-label={t('language.openMenu')}
            title={t('language.openMenu')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="20"
              viewBox="0 0 24 24"
              width="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M2 12h20" />
              <path d="M12 2a15.3 15.3 0 0 1 0 20" />
              <path d="M12 2a15.3 15.3 0 0 0 0 20" />
            </svg>
            <span style={styles.languageCode}>{currentLanguage.shortLabel}</span>
            <span style={styles.chevron} aria-hidden="true">▾</span>
          </button>

          {isLanguageOpen && (
            <div style={styles.languageMenu} role="menu">
              {languages.map((language) => (
                <button
                  type="button"
                  key={language.code}
                  style={{
                    ...styles.languageOption,
                    ...(language.code === currentLanguage.code ? styles.languageOptionActive : {}),
                  }}
                  onClick={() => handleLanguageSelect(language.code)}
                  role="menuitem"
                  aria-label={`${t('language.select')} ${language.label}`}
                >
                  <span style={styles.languageOptionCode}>{language.shortLabel}</span>
                  <span>{language.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          style={styles.themeButton}
          onClick={toggleTheme}
          aria-label={isDark ? t("theme.toggleToLight") : t("theme.toggleToDark")}
          title={isDark ? t("theme.toggleToLight") : t("theme.toggleToDark")}
        >
          {isDark ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2" />
              <path d="M12 20v2" />
              <path d="m4.93 4.93 1.41 1.41" />
              <path d="m17.66 17.66 1.41 1.41" />
              <path d="M2 12h2" />
              <path d="M20 12h2" />
              <path d="m6.34 17.66-1.41 1.41" />
              <path d="m19.07 4.93-1.41 1.41" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
            </svg>
          )}
        </button>

        <a href="mailto:contact@fixanor.com" style={styles.contactLink}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="24"
            viewBox="0 0 24 24"
            width="24"
            fill="currentColor"
            style={styles.icon}
          >
            <path d="M0 0h24v24H0V0z" fill="none" />
            <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
          </svg>
          {!isMobile && 'contact@fixanor.com'}
        </a>
      </div>
    </nav>
  );
};

const styles = {
  navbar: {
    backgroundColor: 'var(--color-navbar-bg)',
    width: '100%',
    height: '5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 1rem',
    boxSizing: 'border-box',
    position: 'relative',
    zIndex: 20,
    borderBottom: '1px solid var(--color-border)',
    boxShadow: 'var(--shadow-navbar)',
  },
  logoLink: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
  },
  navbarHomeImage: {
    height: '4rem',
    maxWidth: '100%',
    objectFit: 'contain',
    filter: 'var(--logo-filter)',
    mixBlendMode: 'var(--asset-blend-mode)',
  },
  contactContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  contactLink: {
    color: 'var(--color-primary)',
    textDecoration: 'none',
    fontWeight: 'bold',
    fontSize: '1.2rem',
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginRight: '0.5rem',
  },
  languageWrapper: {
    position: 'relative',
  },
  languageButton: {
    minWidth: '4.5rem',
    height: '2.35rem',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    backgroundColor: 'var(--color-control-bg)',
    color: 'var(--color-primary)',
    fontWeight: 'bold',
    fontSize: '0.95rem',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.35rem',
    cursor: 'pointer',
    boxShadow: 'var(--shadow-control)',
  },
  languageCode: {
    lineHeight: 1,
  },
  chevron: {
    fontSize: '0.8rem',
    lineHeight: 1,
  },
  languageMenu: {
    position: 'absolute',
    top: 'calc(100% + 0.5rem)',
    right: 0,
    minWidth: '10rem',
    backgroundColor: 'var(--color-menu-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    boxShadow: 'var(--shadow-menu)',
    padding: '0.35rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.15rem',
    zIndex: 30,
  },
  languageOption: {
    width: '100%',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    color: 'var(--color-text)',
    padding: '0.55rem 0.65rem',
    textAlign: 'left',
    fontSize: '0.95rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    cursor: 'pointer',
  },
  languageOptionActive: {
    backgroundColor: 'var(--color-active-bg)',
    color: 'var(--color-primary)',
    fontWeight: 'bold',
  },
  languageOptionCode: {
    minWidth: '1.6rem',
    fontWeight: 'bold',
    color: 'var(--color-primary)',
  },
  themeButton: {
    width: '2.35rem',
    height: '2.35rem',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    backgroundColor: 'var(--color-control-bg)',
    color: 'var(--color-primary)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: 'var(--shadow-control)',
  },
};

export default Navbar;
